import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslations } from 'next-intl';
import Headline from '@components/atoms/headline/headline';
import Image from 'next/image';
import Link from 'next/link';
import track from '@helper/tracking';
import StyledAccessibilityMeta from './accessibilityMeta.style';

import RadioButton from '../../atoms/radio-button/radioButton';
import RangeSlider from '../../atoms/range-slider/rangeSlider';

const AccessibilityItem = ({ title, url, image, trackingData }) => {
  if (!url) return null;

  return (
    <div className="accessibilityMeta__link-item">
      {image && (
        <div className="accessibilityMeta__link-image">
          <Image height={70} width={70} src={image} alt={title} />
        </div>
      )}
      {url && (
        <Link
          href={url}
          onClick={() => {
            track(trackingData);
          }}
        >
          {title ?? ''}
          <span className="accessibilityMeta__link-stretched" />
        </Link>
      )}
    </div>
  );
};

const AccessibilityMeta = ({
  inverted = false,
  currentSiteTheme = 'default',
  updateDefaultTheme = () => {},
  updateBaseFontSize = () => {},
  baseFontSize = 100,
  defaultTheme = false,
  accessibilityData,
  ...props
}) => {
  const t = useTranslations();
  const [trackedSliderInteraction, setTrackedSliderInteraction] = useState(false);

  return (
    <StyledAccessibilityMeta>
      <div className="accessibilityMeta__flyout">
        {accessibilityData && (
          <div className="accessibilityMeta__links">
            <Headline id="modal-title" secondary className="accessibilityMeta__links-headline">
              {t('accessibilityMeta.headline')}
            </Headline>
            <div className="accessibilityMeta__link-container">
              {accessibilityData.atFuturium && (
                <AccessibilityItem
                  title={accessibilityData.atFuturium.title}
                  url={accessibilityData.atFuturium.url}
                  image="/static/accessibility/barrierefreiheit.svg"
                  trackingData={['trackEvent', 'Accessibility Link', 'Click']}
                />
              )}
              {accessibilityData.signLanguage && (
                <AccessibilityItem
                  title={accessibilityData.signLanguage.title}
                  url={accessibilityData.signLanguage.url}
                  image="/static/accessibility/gebaerdensprache.svg"
                  trackingData={['trackEvent', 'Accessibility DGS', 'Click']}
                />
              )}
              {accessibilityData.easyLanguage && (
                <AccessibilityItem
                  title={accessibilityData.easyLanguage.title}
                  url={accessibilityData.easyLanguage.url}
                  image="/static/accessibility/leichte-sprache.svg"
                  trackingData={['trackEvent', 'Accessibility Leichte Sprache', 'Click']}
                />
              )}
              {accessibilityData.digitalStatement && (
                <AccessibilityItem
                  title={accessibilityData.digitalStatement.title}
                  url={accessibilityData.digitalStatement.url}
                  image="/static/accessibility/digitale-barrierefreiheitserklaerung.svg"
                  trackingData={['trackEvent', 'Accessibility Digital', 'Click']}
                />
              )}
            </div>
          </div>
        )}
        <div className="accessibilityMeta__settings">
          <div className="accessibilityMeta__contrast">
            <p className="accessibilityMeta__headline">{t('accessibilityMeta.contrast')}</p>
            <div className="accessibilityMeta__radio-buttons">
              <RadioButton
                name="contrast"
                label={t('accessibilityMeta.colorful')}
                color={currentSiteTheme}
                checked={!defaultTheme}
                onChange={() => {
                  track(['trackEvent', 'Accessibility contrast color', 'Click']);
                  updateDefaultTheme(false);
                }}
              />
              <RadioButton
                name="contrast"
                label={t('accessibilityMeta.monochrome')}
                color="default"
                checked={defaultTheme}
                onChange={() => {
                  track(['trackEvent', 'Accessibility contrast black-white', 'Click']);
                  updateDefaultTheme(true);
                }}
              />
            </div>
          </div>
          <div className="accessibilityMeta__font-size">
            <p className="accessibilityMeta__headline">{t('accessibilityMeta.fontSize')}</p>
            <div className="accessibilityMeta__slider">
              <span className="accessibilityMeta__indicator small">A</span>
              <RangeSlider
                step={5}
                min={100}
                max={170}
                values={[parseInt(baseFontSize, 10)]}
                onChange={value => {
                  if (!trackedSliderInteraction) {
                    track([
                      'trackEvent',
                      'Accessibility Slider Fonts',
                      'Interaction',
                      'Slider Used'
                    ]);
                    setTrackedSliderInteraction(true);
                  }
                  updateBaseFontSize(value[0]);
                }}
                className="accessibilityMeta__range-slider"
              />
              <span className="accessibilityMeta__indicator large">A</span>
            </div>
          </div>
        </div>
      </div>
    </StyledAccessibilityMeta>
  );
};

AccessibilityItem.propTypes = {
  title: PropTypes.string,
  url: PropTypes.string,
  image: PropTypes.string
};

AccessibilityMeta.propTypes = {
  inverted: PropTypes.bool,
  accessibilityData: PropTypes.shape({
    atFuturium: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string
    }),
    digitalStatement: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string
    }),
    easyLanguage: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string
    }),
    signLanguage: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string
    })
  }).isRequired,
  currentSiteTheme: PropTypes.string,
  updateDefaultTheme: PropTypes.func,
  updateBaseFontSize: PropTypes.func,
  baseFontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultTheme: PropTypes.bool
};

const mapStateToProps = state => ({
  accessibilityData: state.app.pageData.accessibility
});

export default connect(mapStateToProps)(AccessibilityMeta);
